import { Booking_Status_Enum, Guideline_Role_Enum, GuidelineFragment } from 'src/state/gql/_types';
import type {
    BookingStatusStateValue,
    BookingStatusEvent,
    BookingStatusContext,
    TimelinePath,
    TimelineStep,
    TransitionDiagnostic,
    GuardFailure,
} from './bookingStatus.types';
import { STATE_METADATA, TRANSITION_METADATA, ROLE_PERMISSIONS, TYPE_PERMISSIONS } from './bookingStatus.types';

/**
 * Static path definitions for each state.
 * Each entry defines what states came before (past) and what comes next (future).
 */
const STATE_PATHS: Record<BookingStatusStateValue, { past: BookingStatusStateValue[]; future: BookingStatusStateValue[] }> = {
    draft: {
        past: [],
        future: ['offered', 'accepted', 'booker_advancing', 'talent_advancing', 'advanced', 'confirmed', 'active', 'completed', 'invoiced', 'paid', 'paid_out']
    },
    offered: {
        past: ['draft'],
        future: ['accepted', 'booker_advancing', 'talent_advancing', 'advanced', 'confirmed', 'active', 'completed', 'invoiced', 'paid', 'paid_out']
    },
    accepted: {
        past: ['draft', 'offered'],
        future: ['booker_advancing', 'talent_advancing', 'advanced', 'confirmed', 'active', 'completed', 'invoiced', 'paid', 'paid_out']
    },
    booker_advancing: {
        past: ['draft', 'offered', 'accepted'],
        future: ['talent_advancing', 'advanced', 'confirmed', 'active', 'completed', 'invoiced', 'paid', 'paid_out']
    },
    talent_advancing: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing'],
        future: ['advanced', 'confirmed', 'active', 'completed', 'invoiced', 'paid', 'paid_out']
    },
    advanced: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing', 'talent_advancing'],
        future: ['confirmed', 'active', 'completed', 'invoiced', 'paid', 'paid_out']
    },
    confirmed: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing', 'talent_advancing', 'advanced'],
        future: ['active', 'completed', 'invoiced', 'paid', 'paid_out']
    },
    active: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing', 'talent_advancing', 'advanced', 'confirmed'],
        future: ['completed', 'invoiced', 'paid', 'paid_out']
    },
    completed: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing', 'talent_advancing', 'advanced', 'confirmed', 'active'],
        future: ['invoiced', 'paid', 'paid_out']
    },
    invoiced: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing', 'talent_advancing', 'advanced', 'confirmed', 'active', 'completed'],
        future: ['paid', 'paid_out']
    },
    paid: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing', 'talent_advancing', 'advanced', 'confirmed', 'active', 'completed', 'invoiced'],
        future: ['paid_out']
    },
    paid_out: {
        past: ['draft', 'offered', 'accepted', 'booker_advancing', 'talent_advancing', 'advanced', 'confirmed', 'active', 'completed', 'invoiced', 'paid'],
        future: []
    },
    cancelled: {
        past: ['draft'],
        future: []
    },
    declined: {
        past: ['draft', 'offered'],
        future: ['draft']
    },
    talent_cancelled: {
        past: ['draft', 'offered', 'accepted'],
        future: ['cancellation_approved', 'cancellation_denied']
    },
    cancellation_approved: {
        past: ['draft', 'offered', 'accepted', 'talent_cancelled'],
        future: ['draft']
    },
    cancellation_denied: {
        past: ['draft', 'offered', 'accepted', 'talent_cancelled'],
        future: ['noshow']
    },
    noshow: {
        past: ['draft', 'offered', 'accepted', 'talent_cancelled', 'cancellation_denied'],
        future: ['draft']
    },
    venue_cancelled: {
        past: ['draft', 'offered', 'accepted'],
        future: ['cancellation', 'invoiced']
    },
    cancellation: {
        past: ['draft', 'offered', 'accepted', 'venue_cancelled'],
        future: []
    },
}
    ;


/**
 * Build a TimelineStep from a state value and status
 */
function buildStep(state: BookingStatusStateValue, status: 'past' | 'current' | 'future'): TimelineStep {
    const meta = STATE_METADATA[state];
    return {
        state,
        label: meta.label,
        description: meta.description,
        icon: meta.icon,
        status,
    };
}

/**
 * Compute the timeline path for the current booking state.
 * Returns ordered steps with past/current/future status and the activeIndex
 * for Mantine's Timeline `active` prop.
 */
export function getTimelinePath(currentState: BookingStatusStateValue): TimelinePath {
    const pathDef = STATE_PATHS[currentState];

    const steps: TimelineStep[] = [
        ...pathDef.past.map(s => buildStep(s, 'past')),
        buildStep(currentState, 'current'),
        ...pathDef.future.map(s => buildStep(s, 'future')),
    ];

    const activeIndex = pathDef.past.length;

    return { steps, activeIndex };
}

/**
 * Which events are defined on each state (regardless of guards)
 */
const STATE_TRANSITIONS: Record<BookingStatusStateValue, BookingStatusEvent['type'][]> = {
    draft: ['OFFER', 'CANCEL'],
    offered: ['ACCEPT', 'DECLINE'],
    accepted: ['TALENT_CANCEL', 'VENUE_CANCEL', 'BOOKER_ADVANCE'],
    booker_advancing: ['TALENT_CANCEL', 'VENUE_CANCEL', 'BOOKER_ADVANCE_TO_TALENT'],
    talent_advancing: ['TALENT_CANCEL', 'VENUE_CANCEL', 'TALENT_ADVANCE'],
    advanced: ['TALENT_CANCEL', 'VENUE_CANCEL', 'RECONFIRM_BOOKING'],
    confirmed: ['START', 'TALENT_CANCEL', 'VENUE_CANCEL'],
    active: ['COMPLETE'],
    completed: ['INVOICE'],
    invoiced: ['PAY'],
    paid: ['PAY_OUT'],
    paid_out: [],
    cancelled: [],
    declined: ['REASSIGN'],
    talent_cancelled: ['APPROVE', 'DENY'],
    cancellation_approved: ['REASSIGN'],
    cancellation_denied: ['WILL_NOSHOW'],
    noshow: ['REASSIGN'],
    venue_cancelled: ['CONFIRM', 'INVOICE'],
    cancellation: [],
};

function checkGuidelinesRequired(guidelines: GuidelineFragment[], status: Booking_Status_Enum): string[] {

    let currentGuidelines = guidelines.filter(g => g.target_booking_status === status);

    let validationErrors = currentGuidelines.reduce((acc, g) => {

        let newErrors: string[] = [];

        switch (g.role) {
            case Guideline_Role_Enum.Info:
                if (g.tenant_required && !g.tenant_confirmed) newErrors.push(`Agency has not confirmed: ${g.name}`)
                if (g.venue_required && !g.venue_confirmed) newErrors.push(`Venue has not confirmed: ${g.name}`)
                if (g.talent_required && !g.talent_confirmed) newErrors.push(`Talent has not confirmed: ${g.name}`)
                break;
            case Guideline_Role_Enum.AgencyTask:
            case Guideline_Role_Enum.VenueTask:
            case Guideline_Role_Enum.TalentTask:
            case Guideline_Role_Enum.AreaTask:
                if (g.tenant_required && !g.tenant_completed) newErrors.push(`Agency has not completed: ${g.name}`)
                if (g.venue_required && !g.venue_completed) newErrors.push(`Venue has not completed: ${g.name}`)
                if (g.talent_required && !g.talent_completed) newErrors.push(`Talent has not completed: ${g.name}`)
                break;
            case Guideline_Role_Enum.AreaQuestion:
            case Guideline_Role_Enum.VenueQuestion:
            case Guideline_Role_Enum.TalentQuestion:
                if (g.tenant_required && !g.tenant_completed) newErrors.push(`Agency has not answered: ${g.name}`)
                if (g.venue_required && !g.venue_completed) newErrors.push(`Venue has not answered: ${g.name}`)
                if (g.talent_required && !g.talent_completed) newErrors.push(`Talent has not answered: ${g.name}`)
                break;
        }

        return acc.concat(newErrors);


    }, [] as string[]);

    return validationErrors;

}

/**
 * Evaluate guard sub-conditions for a specific event type, returning failures.
 *
 * Uses hasPermission() for AND logic (role + type), with superadmin bypass.
 * Adds contextual checks for OFFER (required fields) and ACCEPT/DECLINE/TALENT_CANCEL (assigned talent).
 */
function evaluateGuardFailures(
    event: BookingStatusEvent['type'],
    context: BookingStatusContext,
): GuardFailure[] {
    const { currentUser, currentBooking } = context;
    const failures: GuardFailure[] = [];

    if (!currentUser) {
        failures.push({ category: 'permission', message: 'No user session found' });
        return failures;
    }

    const { role, type } = currentUser;
    const isSuperadmin = role === 'superadmin';
    const roleAllowed = ROLE_PERMISSIONS[role]?.[event] === true;
    const typeAllowed = TYPE_PERMISSIONS[type]?.[event] === true;

    // Check role permission
    if (!roleAllowed) {
        failures.push({ category: 'permission', message: `Your role (${role}) does not have permission for this action` });
    }

    // Check type permission (superadmin bypasses)
    if (!isSuperadmin && !typeAllowed) {
        failures.push({ category: 'permission', message: `Your department (${type}) does not have permission for this action` });
    }

    // Guidelines required checks
    const validateGuidelinesRequired = () => {
        if (currentBooking) {
            let errors = checkGuidelinesRequired(currentBooking.guidelines, currentBooking.status);
            if (errors.length > 0) {
                errors.forEach(error => {
                    failures.push({ category: 'validation', message: error });
                });
            }
        } else {
            failures.push({ category: 'validation', message: 'No booking data available' });
        }
    }

    // Assigned talent checks
    const validateAssignedTalent = () => {
        // Must be the assigned talent (superadmin bypasses)
        if (!isSuperadmin) {
            const isTalent = currentBooking ? currentUser.id === currentBooking.talent_id : false;
            if (!isTalent) {
                failures.push({ category: 'relationship', message: 'Must be the assigned talent for this booking' });
            }
        }
    }

    // Contextual checks for specialized guards
    switch (event) {
        case 'OFFER': {
            if (currentBooking) {
                const missing: string[] = [];
                if (!currentBooking.venue_id) missing.push('Venue');
                if (!currentBooking.area_id) missing.push('Area');
                if (!currentBooking.talent_id) missing.push('Talent');
                if (!currentBooking.date) missing.push('Date');
                if (missing.length > 0) {
                    failures.push({ category: 'validation', message: `Missing selections: ${missing.join(', ')}` });
                }
                validateGuidelinesRequired();
            } else {
                failures.push({ category: 'validation', message: 'No booking data available' });
            }
            break;
        }
        case 'ACCEPT':
            validateAssignedTalent();
            validateGuidelinesRequired();
            break;
        case 'BOOKER_ADVANCE_TO_TALENT':
        case 'TALENT_ADVANCE':
        case 'START':
        case 'COMPLETE':
            validateGuidelinesRequired();
            break;
        
    }

    return failures;
}

/**
 * Get diagnostics for all transitions available on the current state.
 * Returns information about which transitions can fire and why blocked ones are blocked.
 */
export function getTransitionDiagnostics(
    currentState: BookingStatusStateValue,
    context: BookingStatusContext,
): TransitionDiagnostic[] {
    const events = STATE_TRANSITIONS[currentState];

    return events.map(eventType => {
        const failures = evaluateGuardFailures(eventType, context);
        const meta = TRANSITION_METADATA[eventType];

        return {
            event: eventType,
            label: meta.label,
            canTransition: failures.length === 0,
            failures,
        };
    });
}
